/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { ViewModel } from '../../../../../core/viewmodel/ViewModel';
import { EventTypes } from '../../../../../models/application/EventModel';
import { Student } from '../../../../../types/Student';
import { StoreProvider } from '../../../../../stores/StoreProvider';
import * as H from 'history';
import { useLocation, useParams } from 'react-router-dom';
import { CourseService } from '../../../../../services/CourseService';
import { AuthStore } from '../../../../../stores/application/AuthStore';
import { SnackbarStore } from '../../../../../stores/application/SnackbarStore';
import { EventStore } from '../../../../../stores/application/EventStore';
import { ChatStore } from '../../../../../stores/components/ChatStore';
import { getChatImgUrl, getRoomId } from '../../../../../utils/chat';
import { ChatService } from '../../../../../services/ChatService';

export class ChatListViewModel extends ViewModel {
	private routerParams: { courseId: string } = useParams();
	private query = new URLSearchParams(useLocation().search);
	public courseId = this.routerParams.courseId;

	private courseService: CourseService;
	private chatService: ChatService;

	private authStore: AuthStore;
	private snackbarStore: SnackbarStore;
	private eventStore: EventStore;
	private chatStore: ChatStore;

	constructor(storeProvider: StoreProvider, history: H.History) {
		super(storeProvider, history);
		this.courseService = storeProvider.courseService;
		this.chatService = storeProvider.chatService;
		this.authStore = storeProvider.authStore;
		this.snackbarStore = storeProvider.snackbarStore;
		this.eventStore = storeProvider.eventStore;
		this.chatStore = storeProvider.chatStore;
	}

	public areStudentsBeingFetched(): boolean {
		return this.chatStore.areStudentsBeingFetched();
	}

	public getRoomId(studentId: string): string {
		return getRoomId(this.courseId, this.authStore, studentId);
	}

	public getStudentImgSrc(student: Student): string {
		return getChatImgUrl(student);
	}

	public loadActiveChat(): void {
		const chatId = this.query.get('id') ?? this.courseId;

		this.chatStore.setActiveChat(chatId);
	}

	public isActiveChat(key: string): boolean {
		return this.chatStore.isActiveChat(key);
	}

	public getActiveChat(): string {
		return this.chatStore.getActiveChat();
	}

	public resetActiveChat(): void {
		this.chatStore.setActiveChat('');
	}

	public changeActiveChat(key: string): void {
		this.chatStore.setActiveChat(key);
		this.eventStore.addEvent(EventTypes.CHATROOM_CHANGE);
		this.addSearchParams('?id=' + key);

		this.setMessagesAsRead(key);
	}

	private setMessagesAsRead(key: string): void {
		const authToken = this.authStore.getAuthToken();

		this.chatStore.resetUnreadMessages(key);
		this.chatService.setMessagesAsRead(authToken, key).catch(() => {
			this.snackbarStore.showError('chat.set_as_read_error');
		});
	}

	public getStudents(): Student[] {
		return this.chatStore.getStudents();
	}

	public getUnreadMessages(key: string): number {
		return this.chatStore.getUnreadMessagesAmount(key);
	}
}
